/**
 * Dev-mode seed data.
 *
 * When the extension runs outside Tableau (plain `npm run dev`), there is no
 * workbook to discover parameters from. This module synthesises a realistic set
 * of parameters and a back-history of combinations/events so the entire UI —
 * including analytics and trends — can be developed and demoed standalone.
 */

import type {
  Combination,
  ParameterAssignment,
  ParameterDescriptor,
  UsageEvent,
} from "@/types";
import { hashCombination, buildSignature, uid } from "@/lib/utils/id";

export const MOCK_DESCRIPTORS: ParameterDescriptor[] = [
  {
    id: "p_region",
    name: "Region",
    dataType: "string",
    domain: "list",
    controlType: "list",
    currentValue: "Texas",
    required: true,
    allowableValues: [
      { value: "Texas", label: "Texas" },
      { value: "Florida", label: "Florida" },
      { value: "California", label: "California" },
      { value: "New York", label: "New York" },
    ],
  },
  {
    id: "p_year",
    name: "Year",
    dataType: "int",
    domain: "range",
    controlType: "number",
    currentValue: 2025,
    required: true,
    range: { min: 2018, max: 2030, stepSize: 1 },
  },
  {
    id: "p_program",
    name: "Program",
    dataType: "string",
    domain: "list",
    controlType: "list",
    currentValue: "Medicare",
    required: false,
    allowableValues: [
      { value: "Medicare", label: "Medicare" },
      { value: "Medicaid", label: "Medicaid" },
      { value: "Commercial", label: "Commercial" },
    ],
  },
  {
    id: "p_start",
    name: "Start Date",
    dataType: "date",
    domain: "all",
    controlType: "date",
    currentValue: "2025-01-01",
    required: false,
  },
  {
    id: "p_end",
    name: "End Date",
    dataType: "date",
    domain: "all",
    controlType: "date",
    currentValue: "2025-12-31",
    required: false,
  },
  {
    id: "p_threshold",
    name: "Revenue Threshold",
    dataType: "float",
    domain: "range",
    controlType: "number",
    currentValue: 50000,
    required: false,
    range: { min: 0, max: 1_000_000, stepSize: 1000 },
  },
  {
    id: "p_forecast",
    name: "Include Forecast",
    dataType: "bool",
    domain: "all",
    controlType: "boolean",
    currentValue: true,
    required: false,
  },
];

function assignment(
  d: ParameterDescriptor,
  value: string | number | boolean,
): ParameterAssignment {
  const av = d.allowableValues?.find((a) => a.value === value);
  return {
    parameterId: d.id,
    parameterName: d.name,
    dataType: d.dataType,
    value,
    displayValue: av?.label ?? String(value),
  };
}

const USERS = ["jdoe", "asmith", "mlee", "rkhan", "tpatel"];

function makeCombination(
  region: string,
  year: number,
  program: string,
  usageCount: number,
  daysAgo: number,
): { combination: Combination; events: UsageEvent[] } {
  const [rd, yd, pd] = [MOCK_DESCRIPTORS[0], MOCK_DESCRIPTORS[1], MOCK_DESCRIPTORS[2]];
  const assignments = [
    assignment(rd, region),
    assignment(yd, year),
    assignment(pd, program),
  ];
  const id = hashCombination(assignments);
  const lastUsed = new Date(Date.now() - daysAgo * 86_400_000).toISOString();
  const createdAt = new Date(Date.now() - (daysAgo + 60) * 86_400_000).toISOString();

  const events: UsageEvent[] = [];
  const actions: UsageEvent["actionType"][] = [
    "Manual_Go",
    "Top10_Click",
    "Favorite_Click",
    "History_Click",
  ];
  for (let i = 0; i < usageCount; i++) {
    const when = new Date(
      Date.now() - Math.floor(Math.random() * (daysAgo + 30)) * 86_400_000,
    ).toISOString();
    events.push({
      id: uid("evt"),
      combinationId: id,
      signature: buildSignature(assignments),
      actionType: actions[i % actions.length],
      userName: USERS[i % USERS.length],
      workbookName: "Healthcare Analytics",
      dashboardName: "Executive Overview",
      timestamp: when,
      assignments,
    });
  }

  const actionCounts = events.reduce(
    (acc, e) => ({ ...acc, [e.actionType]: (acc[e.actionType] ?? 0) + 1 }),
    {
      Manual_Go: 0,
      Top10_Click: 0,
      Favorite_Click: 0,
      Recent_Click: 0,
      History_Click: 0,
      Recommendation_Click: 0,
    },
  );

  return {
    combination: {
      id,
      assignments,
      signature: buildSignature(assignments),
      usageCount,
      actionCounts,
      uniqueUsers: [...new Set(events.map((e) => e.userName))],
      createdAt,
      createdBy: USERS[0],
      lastUsedAt: lastUsed,
      lastUsedBy: USERS[1],
      workbookName: "Healthcare Analytics",
      dashboardName: "Executive Overview",
    },
    events,
  };
}

export function buildMockHistory(): {
  combinations: Combination[];
  events: UsageEvent[];
} {
  const specs: [string, number, string, number, number][] = [
    ["Texas", 2025, "Medicare", 220, 1],
    ["Florida", 2025, "Medicare", 180, 2],
    ["California", 2024, "Medicaid", 140, 3],
    ["New York", 2025, "Commercial", 95, 1],
    ["Texas", 2024, "Medicaid", 70, 10],
    ["Florida", 2024, "Commercial", 55, 14],
    ["California", 2025, "Medicare", 48, 4],
    ["New York", 2024, "Medicare", 30, 20],
    ["Texas", 2023, "Commercial", 22, 40],
    ["Florida", 2025, "Medicaid", 18, 6],
  ];
  const combinations: Combination[] = [];
  const events: UsageEvent[] = [];
  for (const [r, y, p, u, d] of specs) {
    const { combination, events: evts } = makeCombination(r, y, p, u, d);
    combinations.push(combination);
    events.push(...evts);
  }
  return { combinations, events };
}
